import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { translationJobsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import { Alert } from '@/components/common/Ui'

/**
 * Bulk Tamil translation, from pressing the button to saving what a person
 * approved.
 *
 * The job runs on the worker, so this polls rather than waits: a noun nobody
 * has approved takes seconds from the model and a catalog can carry
 * hundreds. Nothing it shows is saved until the admin approves it, which is
 * why every row is editable and starts unticked... except the ones that came
 * from the glossary, which a person approved already.
 */
export default function BulkTranslate({
  open,
  entityType,
  label,
  // The rows the admin ticked, or null for 'every pending row'. A list of
  // ids is fine for a hand-picked selection; 'all pending' deliberately
  // sends nothing, so a catalog of any size stays a filter on the server.
  selectedIds = null,
  onClose,
  onSaved,
}) {
  const queryClient = useQueryClient()
  const [jobId, setJobId] = useState(null)
  const [edits, setEdits] = useState({})      // itemId -> edited Tamil
  const [picked, setPicked] = useState({})    // itemId -> boolean
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const seeded = useRef(false)

  // A fresh drawer is a fresh job: reopening must not show the last one.
  useEffect(() => {
    if (!open) {
      setJobId(null)
      setEdits({})
      setPicked({})
      setError('')
      setNotice('')
      seeded.current = false
    }
  }, [open])

  const start = useMutation({
    mutationFn: (ids) => translationJobsApi.start(entityType, ids),
    onSuccess: (job) => {
      setError('')
      setJobId(job.id)
      if (job.total === 0) setNotice('Everything here already has Tamil.')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const { data: job } = useQuery({
    queryKey: ['translation-job', jobId],
    queryFn: () => translationJobsApi.get(jobId),
    enabled: Boolean(jobId),
    // Only while the worker still has it. Once it is ready there is nothing
    // left to watch, and polling a finished job forever is just noise.
    refetchInterval: (query) =>
      ['queued', 'running'].includes(query.state.data?.status) ? 1500 : false,
  })

  const running = ['queued', 'running'].includes(job?.status)

  const { data: items } = useQuery({
    queryKey: ['translation-job-items', jobId, job?.status],
    queryFn: () => translationJobsApi.items(jobId),
    enabled: Boolean(jobId) && !running,
  })

  // Tick the rows worth ticking once, when they first arrive: a glossary
  // answer was approved by a person already, so it starts ready to save,
  // while a model's suggestion with a warning on it does not.
  useEffect(() => {
    if (!items || seeded.current) return
    seeded.current = true
    setPicked(
      Object.fromEntries(
        items
          .filter((i) => i.status === 'suggested')
          .map((i) => [i.id, i.origin === 'glossary' || i.warnings.length === 0]),
      ),
    )
  }, [items])

  const reviewable = useMemo(
    () => (items ?? []).filter((i) => i.status === 'suggested'),
    [items],
  )
  const chosen = reviewable.filter((i) => picked[i.id])

  const approve = useMutation({
    mutationFn: (rows) =>
      translationJobsApi.approve(
        jobId,
        rows.map((i) => ({ item_id: i.id, value: edits[i.id] ?? i.suggestion })),
      ),
    onSuccess: (updated) => {
      setError('')
      setNotice(`Saved ${updated.approved} translation(s).`)
      queryClient.invalidateQueries({ queryKey: ['translation-job-items', jobId] })
      queryClient.invalidateQueries({ queryKey: ['translation-job', jobId] })
      onSaved?.()
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const value = (item) => edits[item.id] ?? item.suggestion ?? ''

  return (
    <Drawer
      open={open}
      size="wide"
      title={`Translate ${label} into Tamil`}
      subtitle={
        job
          ? `${job.total} row(s) · ${job.from_glossary} reused · ${job.from_ai} from the model`
          : 'Only rows with no Tamil yet are included.'
      }
      onClose={onClose}
      error={error}
      onErrorDismiss={() => setError('')}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          {reviewable.length > 0 && (
            <>
              <button
                type="button"
                className="btn"
                disabled={approve.isPending || chosen.length === 0}
                onClick={() => approve.mutate(chosen)}
              >
                Approve selected ({chosen.length})
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={approve.isPending}
                onClick={() => approve.mutate(reviewable)}
              >
                {approve.isPending ? 'Saving…' : `Approve all (${reviewable.length})`}
              </button>
            </>
          )}
        </>
      }
    >
      <Alert tone="success" message={notice} onClose={() => setNotice('')} />

      {!jobId && (
        <div className="bulk-start">
          <p className="hint">
            Names are split before anything is asked: sizes, gauges and item
            codes are kept exactly as they are, and only the product name is
            translated. A name somebody has already approved is reused rather
            than sent again.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={start.isPending}
            onClick={() => start.mutate(selectedIds ?? undefined)}
          >
            {start.isPending
              ? 'Starting…'
              : selectedIds
                ? `Translate ${selectedIds.length} selected`
                : 'Translate all pending'}
          </button>
        </div>
      )}

      {running && (
        <div className="bulk-progress">
          <div className="spinner-line" />
          <p className="hint">
            Working through {job.total} row(s). This runs in the background —
            you can leave this open, or come back to it.
          </p>
        </div>
      )}

      {items && reviewable.length === 0 && (
        <p className="hint">
          Nothing left to review{job?.failed ? ` — ${job.failed} could not be translated.` : '.'}
        </p>
      )}

      {reviewable.length > 0 && (
        <table className="table bulk-table">
          <thead>
            <tr>
              <th className="tick">
                <input
                  type="checkbox"
                  aria-label="Select every suggestion"
                  checked={chosen.length === reviewable.length}
                  onChange={(e) =>
                    setPicked(
                      Object.fromEntries(
                        reviewable.map((i) => [i.id, e.target.checked]),
                      ),
                    )
                  }
                />
              </th>
              <th>English</th>
              <th>Tamil</th>
            </tr>
          </thead>
          <tbody>
            {reviewable.map((item) => (
              <tr key={item.id}>
                <td className="tick">
                  <input
                    type="checkbox"
                    aria-label={`Approve ${item.source_text}`}
                    checked={Boolean(picked[item.id])}
                    onChange={(e) =>
                      setPicked((p) => ({ ...p, [item.id]: e.target.checked }))
                    }
                  />
                </td>
                <td>
                  {item.source_text}
                  {item.origin === 'glossary' && (
                    <span className="badge badge-reused" title="A name already approved — no model was asked">
                      reused
                    </span>
                  )}
                </td>
                <td>
                  <input
                    value={value(item)}
                    lang="ta"
                    onChange={(e) =>
                      setEdits((d) => ({ ...d, [item.id]: e.target.value }))
                    }
                  />
                  {item.warnings.map((w) => (
                    <span key={w} className="hint caution">
                      {w}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Drawer>
  )
}
